import React, { useState, useEffect } from "react";
import { Dropdown, Checkbox, Button } from "antd";
import { useDispatch, useSelector } from "react-redux";
import { Assets } from "../../constants/images";
import { GetMembers } from "../../services/team/member";
import { GetAuthUserLocalStorage } from "../../services/localStorage/localStorage";
import {
  addMembersToGroup,
  removeMembersFromGroup,
} from "../../services/chats/frontend-chats";
import { setSelectedUser } from "../../store/slices/chatsSlice";

const AddPeopleSelector = ({
  selectedUser,
  onMembersUpdate,
  className = "",
  style = {},
}) => {
  const dispatch = useDispatch();
  const authUser = GetAuthUserLocalStorage();
  // Check if this is an Everyone group chat
  const isEveryoneGroup = selectedUser?.is_everyone_group === true;

  // State management
  const [allMembers, setAllMembers] = useState([]);
  // store ids as strings for consistency
  const [selectedMemberIds, setSelectedMemberIds] = useState([]);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  // Load all members for the dropdown
  useEffect(() => {
    const fetchMembers = async () => {
      try {
        const response = await GetMembers({
          is_paginated: 0,
          "order-column": "id",
          order: "desc",
        });
        const { data, status } = response.data;
        if (status) {
          setAllMembers(data.users || []);
        }
      } catch (error) {
        console.error("Error loading members:", error);
      }
    };
    fetchMembers();
  }, []);

  // Sync selected members with current group members (fallback to receiver_profile if users_ids empty)
  useEffect(() => {
    if (selectedUser?.users_ids) {
      setSelectedMemberIds(selectedUser.users_ids.map((id) => id.toString()));
    }
  }, [selectedUser?.users_ids]);

  const handleMemberToggle = (memberId) => {
    setSelectedMemberIds((prev) =>
      prev.includes(`${memberId}`)
        ? prev.filter((id) => id !== `${memberId}`)
        : [...prev, `${memberId}`],
    );
  };

  const handleAddMembers = async (selectedMemberIds) => {
    if (!selectedMemberIds) return;

    // derive current members purely from receiver_profile (users_ids may be malformed)
    const currentMemberIds = (selectedUser?.users_ids ?? []).map((id) =>
      id.toString(),
    );

    // Find members to add (selected but not currently in group)
    const membersToAdd = selectedMemberIds
      .filter((id) => !currentMemberIds.includes(id))
      .map((id) => Number(id));

    // Find members to remove (currently in group but not selected)
    const membersToRemove = currentMemberIds
      .filter((id) => !selectedMemberIds.includes(id))
      .map((id) => Number(id));

    try {
      let success = true;

      // Add new members
      if (membersToAdd.length > 0) {
        success = await addMembersToGroup(
          selectedUser?.chat_id,
          membersToAdd,
          dispatch,
        );
      }

      // Remove unselected members
      if (success && membersToRemove.length > 0) {
        success = await removeMembersFromGroup(
          selectedUser?.chat_id,
          membersToRemove,
          dispatch,
        );
      }

      if (success) {
        // Update the selectedUser to reflect the new member list
        const authUserId = GetAuthUserLocalStorage()?.id;

        // Build updated receiver_profile by starting with existing profiles
        const updatedReceiverProfile = [
          ...(selectedUser.receiver_profile || []),
        ];

        // Add new member profiles
        selectedMemberIds.forEach((memberId) => {
          if (!updatedReceiverProfile.find((p) => p.id == memberId)) {
            const memberProfile = allMembers.find(
              (m) => `${m.id}` === `${memberId}`,
            );
            if (memberProfile) {
              updatedReceiverProfile.push({
                id: memberProfile.id.toString(),
                name: memberProfile.name,
                image: memberProfile.image,
              });
            }
          }
        });

        // Ensure current user is included in users_ids without duplicates
        // users_ids rebuilt from selected members + current user
        const finalUsersIds = Array.from(
          new Set([...selectedMemberIds, `${authUserId}`]),
        );

        const updatedSelectedUser = {
          ...selectedUser,
          users_ids: finalUsersIds,
          receiver_profile: updatedReceiverProfile,
        };

        dispatch(setSelectedUser(updatedSelectedUser));

        if (onMembersUpdate) {
          onMembersUpdate(membersToAdd.length, membersToRemove.length);
        }
      }

      return success;
    } catch (error) {
      console.error("Error updating group members:", error);
      return false;
    }
  };

  const handleDoneAddingMembers = async () => {
    await handleAddMembers(selectedMemberIds);
    setDropdownOpen(false);
  };

  const createDropdownMenu = () => {
    const filteredMembers = allMembers
      .filter((member) => `${member.id}` !== `${authUser?.id}`)
      .sort((a, b) => a.name.localeCompare(b.name));

    const items = [
      ...filteredMembers.map((member) => ({
        key: member.id,
        label: (
          <div
            className="add-people-member-item"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              handleMemberToggle(member.id);
            }}
          >
            <div className="add-people-member-name d-flex align-items-center">
              <img
                src={
                  (member.thumbnail ?? member.image) ||
                  Assets.ProfilePlaceholder
                }
                alt={member.name}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "50%",
                  marginRight: 12,
                }}
              />
              <h4
                className="m-0 black"
                style={{ fontSize: "14px", fontWeight: "400", flex: 1 }}
              >
                {member.name}
              </h4>
            </div>
            <Checkbox
              checked={selectedMemberIds.includes(`${member.id}`)}
              onChange={(e) => {
                e.stopPropagation();
                handleMemberToggle(member.id);
              }}
              onClick={(e) => e.stopPropagation()}
              style={{ fontSize: "20px" }}
            />
          </div>
        ),
      })),
    ];

    return { items };
  };

  // Don't render AddPeopleSelector for Everyone group chats
  if (isEveryoneGroup) {
    return null;
  }

  return (
    <div style={{ position: "relative", display: "inline-block", ...style }}>
      <Dropdown
        menu={createDropdownMenu()}
        placement="bottomLeft"
        open={dropdownOpen}
        onOpenChange={setDropdownOpen}
        overlayClassName="add-people-dropdown"
        overlayStyle={{ width: "360px" }}
        trigger={["click"]}
        dropdownRender={(menu) => (
          <div className="add-people-dropdown-container">
            <div className="add-people-menu-wrapper">{menu}</div>
            <div className="add-people-done-btn">
              <Button
                disabled={!selectedMemberIds.length}
                onClick={handleDoneAddingMembers}
                className="w-100"
                type="primary"
              >
                Done
              </Button>
            </div>
          </div>
        )}
      >
        <a
          className={`add-people-link ${className}`}
          onClick={() => setDropdownOpen(!dropdownOpen)}
        >
          Add People
        </a>
      </Dropdown>
    </div>
  );
};

export default AddPeopleSelector;
